/**
 * Trouver et contacter (T1–T4, 03/11/2026): the sequence of a sender in an app,
 * its defaults and its schedule. Shared by the `outreach` edge function, the
 * worker that sends, and the screens. Times are computed in the sequence's
 * time zone, without a date library.
 */

import { nameWords } from './app-object-keys.ts';

export interface OutreachStep {
  /** Working days after the e-mail before it; 0 for the first e-mail. */
  delayDays: number;
  /** The first e-mail's subject; follow-ups answer in its thread (« Re: »). */
  subject: string;
  /** The text, with parameters: {{entreprise}}, {{prenom|Bonjour}}. Written here or by another app (a column). */
  body: string;
}

export interface OutreachSettings {
  /** E-mails a day from this mailbox, all sequences together. */
  dailyLimit: number;
  /** « 09:00 », local time. */
  windowStart: string;
  windowEnd: string;
  /** ISO days of the week (1 = Monday … 7 = Sunday). */
  days: number[];
  timezone: string;
  /** Whom to contact at each company, e.g. « Directeur commercial, Head of Sales ». */
  target: string;
  signature: string;
}

export type OutreachEnrollmentStatus =
  | 'drafting' | 'review' | 'active' | 'replied' | 'bounced' | 'opted_out' | 'stopped' | 'finished' | 'failed';
export type OutreachMessageStatus = 'draft' | 'queued' | 'scheduled' | 'sending' | 'sent' | 'skipped' | 'failed';
export type FoundContactEmailStatus = 'valid' | 'catch_all' | 'risky' | 'invalid' | 'manual';
export type FoundContactEmailSource = 'crm' | 'dropcontact' | 'fullenrich' | 'manual';

/**
 * Cold e-mail practice (03/11 research, `conception-trouver-et-contacter`):
 * a first e-mail and two short follow-ups a few working days apart, sent
 * during office hours on working days, a modest daily volume per mailbox.
 */
export const DEFAULT_OUTREACH_STEPS: OutreachStep[] = [
  { delayDays: 0, subject: '{{entreprise}}', body: 'Bonjour {{prenom|}},\n\n\n\n{{signature}}' },
  { delayDays: 3, subject: '', body: 'Bonjour {{prenom|}},\n\n\n\n{{signature}}' },
  { delayDays: 5, subject: '', body: 'Bonjour {{prenom|}},\n\n\n\n{{signature}}' },
];

export const DEFAULT_OUTREACH_SETTINGS: OutreachSettings = {
  dailyLimit: 30,
  windowStart: '09:00',
  windowEnd: '18:00',
  days: [1, 2, 3, 4, 5],
  timezone: 'Europe/Paris',
  target: '',
  signature: '',
};

/** E-mails that can receive a sequence: found and checked, or given by the person. */
export const SENDABLE_EMAIL_STATUSES: ReadonlySet<FoundContactEmailStatus> = new Set(['valid', 'catch_all', 'manual']);

/** Statuses where the person no longer receives anything from the sequence. */
export const ENDED_ENROLLMENT_STATUSES: ReadonlySet<OutreachEnrollmentStatus> = new Set([
  'replied', 'bounced', 'opted_out', 'stopped', 'finished', 'failed',
]);

/** One person per object and name: « Marie-Hélène Dupont » and « marie helene dupont ». */
export const contactNameKey = (name: string): string => nameWords(name).join(' ').slice(0, 240);

export const normalizeEmail = (value: string): string | null => {
  const email = value.trim().toLowerCase();
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) && email.length <= 320 ? email : null;
};

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const validZone = (zone: string): boolean => {
  try {
    new Intl.DateTimeFormat('fr-FR', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

/** Stored settings read defensively: a missing or broken value takes the default. */
export const parseOutreachSettings = (value: unknown): OutreachSettings => {
  const raw = isRecord(value) ? value : {};
  const text = (key: keyof OutreachSettings) => typeof raw[key] === 'string' ? (raw[key] as string).trim() : null;
  const limit = typeof raw.dailyLimit === 'number' && Number.isInteger(raw.dailyLimit) && raw.dailyLimit > 0 ? raw.dailyLimit : null;
  const start = text('windowStart');
  const end = text('windowEnd');
  const days = Array.isArray(raw.days) ? [...new Set(raw.days.filter((day): day is number => Number.isInteger(day) && day >= 1 && day <= 7))].sort() : [];
  const zone = text('timezone');
  const window = start && end && TIME.test(start) && TIME.test(end) && start < end ? { windowStart: start, windowEnd: end } : null;
  return {
    dailyLimit: limit ?? DEFAULT_OUTREACH_SETTINGS.dailyLimit,
    windowStart: window?.windowStart ?? DEFAULT_OUTREACH_SETTINGS.windowStart,
    windowEnd: window?.windowEnd ?? DEFAULT_OUTREACH_SETTINGS.windowEnd,
    days: days.length > 0 ? days : DEFAULT_OUTREACH_SETTINGS.days,
    timezone: zone && validZone(zone) ? zone : DEFAULT_OUTREACH_SETTINGS.timezone,
    target: text('target') ?? '',
    signature: text('signature') ?? '',
  };
};

export const parseOutreachSteps = (value: unknown): OutreachStep[] => {
  if (!Array.isArray(value)) return DEFAULT_OUTREACH_STEPS;
  const steps = value.flatMap((item, index) => {
    if (!isRecord(item) || typeof item.body !== 'string') return [];
    const delay = typeof item.delayDays === 'number' && Number.isInteger(item.delayDays) && item.delayDays >= 0 ? item.delayDays : 0;
    return [{ delayDays: index === 0 ? 0 : Math.max(delay, 1), subject: typeof item.subject === 'string' ? item.subject : '', body: item.body }];
  });
  return steps.length > 0 ? steps : DEFAULT_OUTREACH_STEPS;
};

// ------------------------------------------------------------
// Parameters (lemlist's variables): {{key}} and {{key|fallback}}.
// ------------------------------------------------------------

export interface OutreachParameter {
  key: string;
  label: string;
  /** Where the value comes from: a column of the app's list, the person found, the sender. */
  from: 'list' | 'person' | 'sender';
  /** A column added by hand to the list for this (removable); the others come from the app or Domino. */
  manual?: boolean;
}

export const PERSON_PARAMETERS: OutreachParameter[] = [
  { key: 'prenom', label: 'Prénom', from: 'person' },
  { key: 'nom', label: 'Nom', from: 'person' },
  { key: 'nom_complet', label: 'Nom complet', from: 'person' },
  { key: 'poste', label: 'Poste', from: 'person' },
  { key: 'email', label: 'E-mail', from: 'person' },
];

export const SENDER_PARAMETERS: OutreachParameter[] = [
  { key: 'expediteur', label: 'Expéditeur', from: 'sender' },
  { key: 'signature', label: 'Signature', from: 'sender' },
];

/** The parameters a sequence can use: the list's columns, then the person and the sender. */
export const outreachParameters = (columns: ReadonlyArray<{ key: string; label: string; manual?: boolean }>): OutreachParameter[] => {
  const reserved = new Set([...PERSON_PARAMETERS, ...SENDER_PARAMETERS].map((parameter) => parameter.key));
  return [
    ...columns.filter((column) => !reserved.has(column.key))
      .map((column) => ({ key: column.key, label: column.label, from: 'list' as const, ...(column.manual ? { manual: true } : {}) })),
    ...PERSON_PARAMETERS,
    ...SENDER_PARAMETERS,
  ];
};

const TEMPLATE = /\{\{\s*([a-zA-Z0-9_]+)\s*(?:\|([^}]*))?\}\}/g;

/** Every parameter a text names, once, in order. */
export const templateKeys = (text: string): string[] => [...new Set([...text.matchAll(TEMPLATE)].map((match) => match[1]!))];

/**
 * A text with its parameters replaced. A parameter without a value takes its
 * fallback (« {{prenom|Bonjour}} »; an empty fallback « {{prenom|}} » leaves
 * nothing); without one, it is missing: the prospect is not ready.
 */
export const renderTemplate = (text: string, values: Readonly<Record<string, string>>): { text: string; missing: string[] } => {
  const missing: string[] = [];
  const rendered = text.replace(TEMPLATE, (_match, key: string, fallback: string | undefined) => {
    const value = values[key]?.trim();
    if (value) return value;
    if (fallback !== undefined) return fallback;
    if (!missing.includes(key)) missing.push(key);
    return `{{${key}}}`;
  });
  return { text: rendered, missing };
};

/** The values of one prospect: the object's cells, the person, the sender. */
export const outreachValues = (params: {
  cells: ReadonlyArray<{ key: string; value: unknown }>;
  person: { fullName: string; jobTitle: string | null; email: string | null };
  senderName: string;
  signature: string;
}): Record<string, string> => {
  const values: Record<string, string> = {};
  for (const cell of params.cells) {
    if (typeof cell.value === 'string' || typeof cell.value === 'number') values[cell.key] = String(cell.value);
  }
  const [first = '', ...rest] = params.person.fullName.trim().split(/\s+/);
  return {
    ...values,
    prenom: first,
    nom: rest.join(' '),
    nom_complet: params.person.fullName.trim(),
    poste: params.person.jobTitle ?? '',
    email: params.person.email ?? '',
    expediteur: params.senderName,
    signature: params.signature || params.senderName,
  };
};

/** A manual column's key, from its label: « Accroche perso » → accroche_perso. */
export const parameterKey = (label: string): string => nameWords(label).join('_').slice(0, 60);

// ------------------------------------------------------------
// Time, in the sequence's zone.
// ------------------------------------------------------------

interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  /** ISO day of the week. */
  weekday: number;
}

const WEEKDAYS: Record<string, number> = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };

export const localParts = (date: Date, zone: string): LocalParts => {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short', hourCycle: 'h23',
  }).formatToParts(date).map((part) => [part.type, part.value]));
  return {
    year: Number(parts.year), month: Number(parts.month), day: Number(parts.day),
    hour: Number(parts.hour), minute: Number(parts.minute), weekday: WEEKDAYS[parts.weekday ?? ''] ?? 1,
  };
};

/** The instant of a local date and time in a zone (two passes cover the change of hour). */
export const zonedInstant = (year: number, month: number, day: number, minutes: number, zone: string): Date => {
  const wanted = Date.UTC(year, month - 1, day, Math.floor(minutes / 60), minutes % 60);
  let guess = wanted;
  for (let pass = 0; pass < 2; pass += 1) {
    const local = localParts(new Date(guess), zone);
    const seen = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute);
    guess += wanted - seen;
  }
  return new Date(guess);
};

const minutesOf = (time: string): number => {
  const [hours = '0', minutes = '0'] = time.split(':');
  return Number(hours) * 60 + Number(minutes);
};

/** « 2026-11-04 » in the zone: the day an e-mail counts against the daily limit. */
export const localDayKey = (date: Date, zone: string): string => {
  const local = localParts(date, zone);
  return `${local.year}-${String(local.month).padStart(2, '0')}-${String(local.day).padStart(2, '0')}`;
};

const addLocalDays = (local: LocalParts, days: number): { year: number; month: number; day: number; weekday: number } => {
  const date = new Date(Date.UTC(local.year, local.month - 1, local.day + days));
  return { year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate(), weekday: ((local.weekday - 1 + days) % 7 + 7) % 7 + 1 };
};

/**
 * Times for `count` new e-mails from `from`: on the allowed days, inside the
 * window, evenly spread so that a day never holds more than the daily limit
 * (`taken` counts what is already planned or sent per local day).
 */
export const planSendSlots = (params: {
  from: Date;
  count: number;
  settings: OutreachSettings;
  taken?: ReadonlyMap<string, number>;
}): Date[] => {
  const { settings } = params;
  const start = minutesOf(settings.windowStart);
  const end = minutesOf(settings.windowEnd);
  const gap = (end - start) / settings.dailyLimit;
  const slots: Date[] = [];
  const today = localParts(params.from, settings.timezone);
  for (let offset = 0; slots.length < params.count; offset += 1) {
    const day = addLocalDays(today, offset);
    if (!settings.days.includes(day.weekday)) continue;
    const key = `${day.year}-${String(day.month).padStart(2, '0')}-${String(day.day).padStart(2, '0')}`;
    let used = params.taken?.get(key) ?? 0;
    for (let index = 0; index < settings.dailyLimit && slots.length < params.count; index += 1) {
      const at = zonedInstant(day.year, day.month, day.day, Math.round(start + index * gap), settings.timezone);
      if (at.getTime() < params.from.getTime()) continue;
      if (used >= settings.dailyLimit) break;
      used += 1;
      slots.push(at);
    }
  }
  return slots;
};

/**
 * When a follow-up is due: `delayDays` allowed days after the e-mail before it,
 * at the same local time, brought inside the window.
 */
export const followUpTime = (sentAt: Date, delayDays: number, settings: OutreachSettings): Date => {
  const local = localParts(sentAt, settings.timezone);
  let counted = 0;
  let offset = 0;
  let day = addLocalDays(local, 0);
  while (counted < delayDays) {
    offset += 1;
    day = addLocalDays(local, offset);
    if (settings.days.includes(day.weekday)) counted += 1;
  }
  const minutes = Math.min(Math.max(local.hour * 60 + local.minute, minutesOf(settings.windowStart)), minutesOf(settings.windowEnd) - 1);
  return zonedInstant(day.year, day.month, day.day, minutes, settings.timezone);
};

/** Where an address came from, as the first e-mail says it (article 14 GDPR). */
export const EMAIL_SOURCE_LABELS: Record<FoundContactEmailSource, string> = {
  crm: 'notre CRM',
  dropcontact: 'Dropcontact',
  fullenrich: 'FullEnrich',
  manual: 'nos recherches',
};

/** The two lines the code adds under every first e-mail: how to refuse, where the address came from. */
export const optOutFooter = (params: { link: string; source: FoundContactEmailSource }): string =>
  `\n\n—\nSi vous ne souhaitez plus recevoir de message de ma part, répondez simplement « non » ou cliquez ici : ${params.link}\n`
  + `Votre adresse professionnelle vient de ${EMAIL_SOURCE_LABELS[params.source]}.`;
